/**
 * Couche service « Utilisateurs & Rôles » (cloisonnée par école côté serveur).
 *
 * Un compte créé par un administrateur reçoit un mot de passe PROVISOIRE généré par le serveur : il
 * n'est renvoyé qu'une fois et doit être changé à la première connexion.
 */
import { assetUrl, http, orUndefined, storeImage } from "./http";
import type { AppUser, CycleScope, Permission, Role } from "@/types";

/* -------------------------------------------------------------------------- */
/* Format du backend                                                           */
/* -------------------------------------------------------------------------- */

interface ApiRoleSummary {
  id: number;
  name: string;
  scope: CycleScope;
  cycleId: number | null;
}

interface ApiRole extends ApiRoleSummary {
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  usersCount: number;
}

interface ApiUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  avatarUrl: string | null;
  role: ApiRoleSummary;
  isActive: boolean;
  createdAt: string;
}

interface ApiPermissionGroup {
  resource: string;
  label: string;
  group: string;
  permissions: Array<{ key: string; action: string; label: string; globalOnly: boolean }>;
}

export interface PermissionModule {
  module: string;
  label: string;
  /** Menu de rattachement (Finances, Scolarité, Ressources humaines, Système). */
  group: string;
  icon: string;
  permissions: Permission[];
}

/** Icône de chaque ressource dans la matrice des permissions. */
const MODULE_ICONS: Record<string, string> = {
  school: "ri-bank-line",
  settings: "ri-settings-3-line",
  cycles: "ri-stack-line",
  "school-years": "ri-calendar-line",
  classes: "ri-building-4-line",
  subjects: "ri-book-2-line",
  teachers: "ri-user-star-line",
  staff: "ri-team-line",
  students: "ri-user-3-line",
  guardians: "ri-parent-line",
  enrollments: "ri-file-add-line",
  payments: "ri-money-dollar-circle-line",
  dossiers: "ri-folder-user-line",
  revenues: "ri-arrow-up-circle-line",
  "finance-categories": "ri-price-tag-3-line",
  expenses: "ri-arrow-down-circle-line",
  charges: "ri-stack-line",
  payroll: "ri-hand-coin-line",
  fees: "ri-file-text-line",
  "staff-functions": "ri-briefcase-line",
  salaries: "ri-wallet-3-line",
  reports: "ri-line-chart-line",
  activity: "ri-history-line",
  users: "ri-group-line",
  roles: "ri-shield-keyhole-line",
};

export function toAppUser(u: ApiUser): AppUser {
  return {
    id: String(u.id),
    firstName: u.firstName,
    lastName: u.lastName,
    email: u.email,
    phone: u.phone ?? undefined,
    avatar: assetUrl(u.avatarUrl) || undefined,
    roleId: String(u.role.id),
    roleName: u.role.name,
    cycleScope: u.role.scope,
    isActive: u.isActive,
    createdAt: u.createdAt.slice(0, 10),
  };
}

function toRole(r: ApiRole): Role {
  return {
    id: String(r.id),
    name: r.name,
    description: r.description ?? "",
    cycleScope: r.scope,
    permissions: r.permissions,
    userCount: r.usersCount,
    isSystem: r.isSystem,
  };
}

/* -------------------------------------------------------------------------- */
/* Permissions                                                                 */
/* -------------------------------------------------------------------------- */

export async function listPermissionModules(): Promise<PermissionModule[]> {
  const groups = await http.get<ApiPermissionGroup[]>("/permissions");
  return groups.map((g) => ({
    module: g.resource,
    label: g.label,
    group: g.group,
    icon: MODULE_ICONS[g.resource] ?? "ri-checkbox-multiple-line",
    permissions: g.permissions.map((p) => ({
      id: p.key,
      module: g.resource,
      action: p.action,
      label: p.label,
      globalOnly: p.globalOnly,
    })),
  }));
}

/* -------------------------------------------------------------------------- */
/* Utilisateurs                                                                */
/* -------------------------------------------------------------------------- */

export interface UserDto {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  /** Photo : data URL choisie sur le poste, URL du serveur, ou vide pour l'effacer. */
  avatar?: string;
  roleId: string;
  isActive: boolean;
}

/** Tous les comptes de l'école. */
export async function listUsers(): Promise<AppUser[]> {
  return (await http.getAll<ApiUser>("/users")).map(toAppUser);
}

/** Crée un compte ; renvoie le mot de passe provisoire (affiché une seule fois). */
export async function createUser(
  dto: UserDto,
): Promise<{ user: AppUser; temporaryPassword: string; emailSent: boolean }> {
  const created = await http.post<ApiUser & { temporaryPassword: string; emailSent: boolean }>("/users", {
    email: dto.email.trim(),
    firstName: dto.firstName.trim(),
    lastName: dto.lastName.trim(),
    phone: orUndefined(dto.phone),
    avatarUrl: await storeImage(dto.avatar),
    roleId: Number(dto.roleId),
  });
  let user = toAppUser(created);
  // Un compte se crée actif : la désactivation éventuelle suit.
  if (!dto.isActive) user = await updateUser(user.id, { isActive: false });
  return { user, temporaryPassword: created.temporaryPassword, emailSent: created.emailSent };
}

/** Modification par un administrateur (identité, rôle, statut). Téléphone ou photo vides = effacés. */
export async function updateUser(id: string, dto: Partial<UserDto>): Promise<AppUser> {
  return toAppUser(
    await http.put<ApiUser>(`/users/${id}`, {
      email: dto.email?.trim(),
      firstName: dto.firstName?.trim(),
      lastName: dto.lastName?.trim(),
      phone: dto.phone === undefined ? undefined : orUndefined(dto.phone) ?? null,
      avatarUrl: dto.avatar === undefined ? undefined : (await storeImage(dto.avatar)) ?? null,
      roleId: dto.roleId === undefined ? undefined : Number(dto.roleId),
      isActive: dto.isActive,
    }),
  );
}

/** Nouveau mot de passe provisoire (la session de l'utilisateur est révoquée), aussi envoyé par e-mail. */
export function issueTemporaryPassword(id: string): Promise<{ temporaryPassword: string; emailSent: boolean }> {
  return http.post<{ temporaryPassword: string; emailSent: boolean }>(`/users/${id}/temporary-password`);
}

export async function deleteUser(id: string): Promise<void> {
  await http.delete<null>(`/users/${id}`);
}

/* -------------------------------------------------------------------------- */
/* Rôles                                                                       */
/* -------------------------------------------------------------------------- */

export interface RoleDto {
  name: string;
  description: string;
  cycleScope: CycleScope;
  permissions: string[];
}

function roleBody(dto: RoleDto) {
  return {
    name: dto.name.trim(),
    description: orUndefined(dto.description),
    scope: dto.cycleScope,
    permissions: dto.permissions,
  };
}

export async function listRoles(): Promise<Role[]> {
  return (await http.getAll<ApiRole>("/roles")).map(toRole);
}

export async function createRole(dto: RoleDto): Promise<Role> {
  return toRole(await http.post<ApiRole>("/roles", roleBody(dto)));
}

export async function updateRole(id: string, dto: RoleDto): Promise<Role> {
  return toRole(await http.put<ApiRole>(`/roles/${id}`, roleBody(dto)));
}

export async function deleteRole(id: string): Promise<void> {
  await http.delete<null>(`/roles/${id}`);
}
